import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { isPricingLive } from "@/config/pricing";
import {
  LayoutDashboard, Sparkles, Globe2, FlaskConical, LineChart, MessageCircle,
  User as UserIcon, LogOut, LogIn, Home, Gavel, Tag, GraduationCap,
} from "lucide-react";
import { startGoogleLogin } from "@/lib/login";

// Sidebar groups — terminology-only update. Routes, components, and behaviour are
// unchanged; only the labels surfaced to users are renamed (see PRD entry "Sidebar
// terminology refresh").
const GROUPS_PUBLIC = [
  {
    title: null,
    items: [
      { to: "/", icon: Home, label: "Home" },
    ],
  },
  {
    title: "EXPLORE",
    items: [
      { to: "/universities", icon: Globe2, label: "University Atlas" },
      { to: "/compare", icon: FlaskConical, label: "Decision Lab" },
    ],
  },
  {
    title: "DECISION",
    items: [
      { to: "/top-picks", icon: Sparkles, label: "Decision Engine" },
      { to: "/decision-report", icon: Gavel, label: "Final Verdict" },
    ],
  },
  {
    title: "PLAN",
    items: [
      { to: "/financial", icon: LineChart, label: "Investment Intelligence" },
      { to: "/advisor", icon: MessageCircle, iconUrl: "/cards/mentor-menu.webp", label: "Unimora AI Mentor" },
    ],
  },
];

const GROUPS_AUTH = [
  {
    title: "DECISION",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Decision Desk" },
      { to: "/top-picks", icon: Sparkles, label: "Decision Engine" },
      { to: "/decision-report", icon: Gavel, label: "Final Verdict" },
    ],
  },
  {
    title: "EXPLORE",
    items: [
      { to: "/universities", icon: Globe2, label: "University Atlas" },
      { to: "/compare", icon: FlaskConical, label: "Decision Lab" },
    ],
  },
  {
    title: "PLAN",
    items: [
      { to: "/financial", icon: LineChart, label: "Investment Intelligence" },
      { to: "/advisor", icon: MessageCircle, iconUrl: "/cards/mentor-menu.webp", label: "Unimora AI Mentor" },
    ],
  },
];

// Flat list of all sidebar links — used for the mobile horizontal scroll strip.
const flatten = (groups) => groups.flatMap(g => g.items);

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const groups = user ? GROUPS_AUTH : GROUPS_PUBLIC;
  const mobileItems = flatten(groups);
  const showUpgradePrompt = isPricingLive() && user && !user?.is_paid;
  const showPaidBadge = user?.is_paid === true;

  const startGoogle = () => {
    startGoogleLogin("/dashboard");
  };

  const isActive = (to) => {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(to + "/");
  };

  return (
    <>
      {/* DESKTOP — fixed left sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-[240px] border-r border-[var(--s200)] bg-white z-40 flex-col">
        <Link to="/" className="block px-4 py-4 border-b border-[var(--s200)]" data-testid="nav-logo">
          <div className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt="Unimora.in"
              className="w-12 h-12 rounded-[10px] object-cover shrink-0"
            />
            <div className="leading-none">
              <div className="text-[22px] font-bold tracking-tight text-[var(--s900)]">
                Unimora<span className="text-[var(--ind)]">.in</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.04em] text-[var(--s700)]">
                <GraduationCap size={12} strokeWidth={2.2} className="text-[var(--ind)]" />
                <span>Dream. Decide. Achieve.</span>
              </div>
            </div>
          </div>
        </Link>

        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          {groups.map((group, gi) => (
            <div key={gi} className={gi > 0 ? "mt-5" : ""}>
              {group.title && (
                <div className="px-3 mb-1.5 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]" data-testid={`nav-group-${group.title.toLowerCase()}`}>
                  {group.title}
                </div>
              )}
              <div className="space-y-1">
                {group.items.map(({ to, icon: Icon, iconUrl, label }) => (
                  <Link
                    key={to}
                    to={to}
                    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-[var(--r8)] text-body-sm font-medium transition-colors ${
                      isActive(to)
                        ? "bg-[var(--ind)] text-white"
                        : "text-[var(--s700)] hover:bg-[var(--s100)] hover:text-[var(--s900)]"
                    }`}
                  >
                    {iconUrl ? (
                      <img
                        src={iconUrl}
                        alt=""
                        aria-hidden
                        className="w-5 h-5 object-contain shrink-0"
                      />
                    ) : (
                      <Icon size={17} strokeWidth={1.8} />
                    )}
                    <span>{label}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {showUpgradePrompt && (
          <div
            data-testid="nav-upgrade-prompt"
            style={{
              background: "var(--ind-soft)",
              borderRadius: "10px",
              padding: "14px",
              margin: "8px",
            }}
          >
            <p style={{ fontSize: "13px", fontWeight: 600, color: "var(--s900)", marginBottom: "2px" }}>
              Unlock full access
            </p>
            <p style={{ fontSize: "11px", color: "var(--s500)", marginBottom: "10px", lineHeight: 1.5 }}>
              Decision Engine · Financial Plan · AI Counsel
            </p>
            <button
              type="button"
              onClick={() => nav("/pricing")}
              style={{
                width: "100%",
                background: "var(--ind)",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "8px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              See pricing →
            </button>
          </div>
        )}

        <div className="border-t border-[var(--s200)] p-3">
          {user ? (
            <div className="space-y-1">
              <div className="px-3 mb-1.5 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]" data-testid="nav-group-profile">
                PROFILE
              </div>
              <Link
                to="/profile"
                data-testid="nav-student-profile"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-[var(--r8)] text-body-sm font-medium transition-colors ${
                  pathname === "/profile"
                    ? "bg-[var(--ind)] text-white"
                    : "text-[var(--s700)] hover:bg-[var(--s100)]"
                }`}
              >
                <UserIcon size={17} strokeWidth={1.8} /> Student Profile
              </Link>
              <div className="flex items-center gap-3 px-3 py-2">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full border border-[var(--s200)]" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[var(--ind-soft)] text-[var(--ind)] flex items-center justify-center">
                    <UserIcon size={13} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-label font-medium text-[var(--s900)] truncate flex items-center gap-1.5">
                    <span className="truncate">{user.name}</span>
                    {showPaidBadge && (
                      <span
                        data-testid="nav-paid-badge"
                        style={{
                          background: "var(--ind-soft)",
                          color: "var(--ind)",
                          fontSize: "10px",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                          padding: "2px 8px",
                          borderRadius: "999px",
                          flexShrink: 0,
                        }}
                      >
                        Full Access
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => { logout(); nav("/"); }}
                  data-testid="nav-logout"
                  className="text-[var(--s500)] hover:text-[var(--ind)] p-1"
                  title="Sign out"
                >
                  <LogOut size={15} />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={startGoogle}
              data-testid="nav-signin"
              className="w-full flex items-center justify-center gap-2 btn-primary"
            >
              <LogIn size={14} /> Start free
            </button>
          )}
        </div>
      </aside>

      {/* MOBILE — compact top bar with icon-only nav */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-[var(--s200)]">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo-mobile">
            <img src="/logo.png" alt="Unimora.in" className="w-8 h-8 rounded-[8px] object-cover shrink-0" />
            <div className="leading-none">
              <div className="text-[16px] font-bold tracking-tight text-[var(--s900)]">
                Unimora<span className="text-[var(--ind)]">.in</span>
              </div>
              <div className="mt-1 flex items-center gap-1 text-[9px] font-semibold tracking-[0.04em] text-[var(--s700)]">
                <GraduationCap size={10} strokeWidth={2.2} className="text-[var(--ind)]" />
                <span>Dream. Decide. Achieve.</span>
              </div>
            </div>
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              {showPaidBadge && (
                <span
                  data-testid="nav-paid-badge-mobile"
                  style={{
                    background: "var(--ind-soft)",
                    color: "var(--ind)",
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    padding: "2px 8px",
                    borderRadius: "999px",
                  }}
                >
                  Full Access
                </span>
              )}
              <button
                onClick={() => { logout(); nav("/"); }}
                className="text-[var(--s500)] p-1.5"
                data-testid="nav-logout-mobile"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button onClick={startGoogle} className="btn-primary text-label" data-testid="nav-signin-mobile">Start free</button>
          )}
        </div>
        {showUpgradePrompt && (
          <button
            type="button"
            onClick={() => nav("/pricing")}
            data-testid="nav-upgrade-prompt-mobile"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "calc(100% - 24px)",
              margin: "0 12px 8px",
              background: "var(--ind-soft)",
              border: "none",
              borderRadius: "8px",
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--s900)" }}>Unlock full access</span>
            <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--ind)" }}>See pricing →</span>
          </button>
        )}
        <div className="flex items-center gap-1 px-3 pb-2 overflow-x-auto">
          {mobileItems.map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              title={label}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--r8)] text-label font-medium ${
                isActive(to)
                  ? "bg-[var(--ind)] text-white"
                  : "text-[var(--s600)] bg-[var(--s100)]"
              }`}
            >
              <Icon size={14} strokeWidth={2} /> {label}
            </Link>
          ))}
        </div>
      </header>
    </>
  );
}
